/* Brushes: Hülle aus Punkten, Flächenattribute, Transformationen, Clip. */
import type { BBox, Brush, Face, Mat3, Vec3 } from './types.ts';
import { add, cross, det3, dot, inv3, len, mirrorMat, mul, mulMV, norm, rotMat, sub, transpose } from './vec.ts';
import { newId } from './ids.ts';

// ------------------------------------------------------------ Flächenattribute
export function defaultAxes(n: Vec3): { ua: Vec3; va: Vec3 } {
  const ax = Math.abs(n[0]), ay = Math.abs(n[1]), az = Math.abs(n[2]);
  if (az >= ax && az >= ay) return { ua: [1, 0, 0], va: [0, -1, 0] };
  if (ax >= ay) return { ua: [0, 1, 0], va: [0, 0, -1] };
  return { ua: [1, 0, 0], va: [0, 0, -1] };
}
/** Texturachsen in die Flächenebene gelegt, senkrecht zueinander (keine Streckung, keine Scherung). Die Richtung von u und die Seite von v bleiben erhalten;
 *  fallen die Achsen dabei zusammen, dienen die Weltachsen der Fläche als Ausgang. Achsparallele Flächen bleiben unverändert. */
export function alignedAxes(n: Vec3, ua: Vec3, va: Vec3): { ua: Vec3; va: Vec3 } {
  const proj = (a: Vec3): Vec3 => { const d = dot(a, n); return [a[0] - n[0] * d, a[1] - n[1] * d, a[2] - n[2] * d]; };
  let u = proj(ua), v = proj(va);
  if (len(u) < 1e-6 || len(v) < 1e-6 || Math.abs(dot(norm(u), norm(v))) > 0.999) { const d = defaultAxes(n); u = proj(d.ua); v = proj(d.va); }
  u = norm(u);
  let w = cross(n, u);
  if (dot(w, v) < 0) w = [-w[0], -w[1], -w[2]];
  const r = (x: Vec3): Vec3 => [Math.round(x[0] * 1e4) / 1e4 + 0, Math.round(x[1] * 1e4) / 1e4 + 0, Math.round(x[2] * 1e4) / 1e4 + 0];
  return { ua: r(u), va: r(norm(w)) };
}
export function defaultFace(tex: string | undefined, n: Vec3): Omit<Face, 'vi'> {
  const a = defaultAxes(n);
  return { tex: tex || 'NULL', ua: a.ua, uo: 0, va: a.va, vo: 0, rot: 0, xs: 1, ys: 1 };
}

// ------------------------------------------------------------ Hülle
export function faceNormal(verts: Vec3[], vi: number[]): Vec3 {
  let nx = 0, ny = 0, nz = 0;
  for (let i = 0; i < vi.length; i++) {
    const a = verts[vi[i]], b = verts[vi[(i + 1) % vi.length]];
    nx += (a[1] - b[1]) * (a[2] + b[2]);
    ny += (a[2] - b[2]) * (a[0] + b[0]);
    nz += (a[0] - b[0]) * (a[1] + b[1]);
  }
  return norm([nx, ny, nz]);
}

interface Candidate { n: Vec3; d: number; on: number[] }
interface Pt2 { i: number; x: number; y: number }

/** Konvexe Hülle kleiner Punktmengen. Liefert { verts, loops } oder null (flach/zu wenig Punkte). */
export function buildHull(points: Vec3[], tol?: number): { verts: Vec3[]; loops: number[][] } | null {
  const tl = tol || 0.01;
  const pts: Vec3[] = [], seen = new Set<string>();
  for (const p of points) {
    const k = Math.round(p[0] * 1000) + ',' + Math.round(p[1] * 1000) + ',' + Math.round(p[2] * 1000);
    if (!seen.has(k)) { seen.add(k); pts.push([p[0], p[1], p[2]]); }
  }
  const n = pts.length;
  if (n < 4) return null;
  const cand: Candidate[] = [], keys = new Set<string>();
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) for (let k = j + 1; k < n; k++) {
    const nr = cross(sub(pts[j], pts[i]), sub(pts[k], pts[i]));
    const l = len(nr);
    if (l < 1e-9) continue;
    const nn: Vec3 = [nr[0] / l, nr[1] / l, nr[2] / l], d = dot(nn, pts[i]);
    let pos = false, neg = false;
    for (let m = 0; m < n; m++) {
      const s = dot(nn, pts[m]) - d;
      if (s > tl) pos = true; else if (s < -tl) neg = true;
      if (pos && neg) break;
    }
    if (pos && neg) continue;
    if (!pos && !neg) continue; // alle Punkte komplanar
    const N: Vec3 = pos ? [-nn[0], -nn[1], -nn[2]] : nn, D = pos ? -d : d;
    const on: number[] = [];
    for (let m = 0; m < n; m++) if (Math.abs(dot(N, pts[m]) - D) <= tl) on.push(m);
    const key = on.join(',');
    if (keys.has(key)) continue;
    keys.add(key);
    cand.push({ n: N, d: D, on: on });
  }
  cand.sort((a, b) => b.on.length - a.on.length);
  const acc: (Candidate & { set: Set<number> })[] = [];
  for (const c of cand) {
    const set = new Set(c.on);
    let sub_ = false;
    for (const a of acc) { if (c.on.every((x) => a.set.has(x))) { sub_ = true; break; } }
    if (!sub_) acc.push({ n: c.n, d: c.d, on: c.on, set: set });
  }
  if (acc.length < 4) return null;
  const loops: number[][] = [], used = new Map<number, number>(), verts: Vec3[] = [];
  for (const a of acc) {
    const P = a.on.map((i) => pts[i]);
    let c: Vec3 = [0, 0, 0];
    P.forEach((p) => { c = add(c, p); });
    c = mul(c, 1 / P.length);
    const ref: Vec3 = Math.abs(a.n[0]) < 0.6 ? [1, 0, 0] : [0, 1, 0];
    const u = norm(cross(a.n, ref)), v = cross(a.n, u);
    const q: Pt2[] = a.on.map((i, idx) => ({ i: i, x: dot(sub(P[idx], c), u), y: dot(sub(P[idx], c), v) }));
    q.sort((p1, p2) => p1.x - p2.x || p1.y - p2.y);
    const cr = (o: Pt2, p1: Pt2, p2: Pt2) => (p1.x - o.x) * (p2.y - o.y) - (p1.y - o.y) * (p2.x - o.x);
    const lower: Pt2[] = [];
    for (const p of q) { while (lower.length >= 2 && cr(lower[lower.length - 2], lower[lower.length - 1], p) <= 1e-9) lower.pop(); lower.push(p); }
    const upper: Pt2[] = [];
    for (let t = q.length - 1; t >= 0; t--) { const p = q[t]; while (upper.length >= 2 && cr(upper[upper.length - 2], upper[upper.length - 1], p) <= 1e-9) upper.pop(); upper.push(p); }
    lower.pop(); upper.pop();
    const ring = lower.concat(upper);
    if (ring.length < 3) continue;
    loops.push(ring.map((p) => {
      if (!used.has(p.i)) { used.set(p.i, verts.length); verts.push(pts[p.i]); }
      return used.get(p.i) as number;
    }));
  }
  if (loops.length < 4) return null;
  return { verts: verts, loops: loops };
}

export function makeBrush(points: Vec3[], opts?: { tol?: number; id?: string; tex?: string }): Brush | null {
  opts = opts || {};
  const h = buildHull(points, opts.tol);
  if (!h) return null;
  return {
    id: opts.id || newId(), verts: h.verts,
    faces: h.loops.map((vi) => Object.assign({ vi: vi }, defaultFace(opts.tex, faceNormal(h.verts, vi))))
  };
}

/** Neu aufbauen aus Punkten, Flächenattribute vom ähnlichsten Alt-Brush-Face übernehmen. */
export function rebuildBrush(old: Brush, points: Vec3[], opts?: { tol?: number; tex?: string }): Brush | null {
  opts = opts || {};
  const h = buildHull(points, opts.tol);
  if (!h) return null;
  const oldN = old.faces.map((f) => faceNormal(old.verts, f.vi));
  const faces = h.loops.map((vi) => {
    const nn = faceNormal(h.verts, vi);
    let bi = 0, bd = -2;
    oldN.forEach((o, i) => { const d = dot(o, nn); if (d > bd) { bd = d; bi = i; } });
    const src = old.faces[bi];
    // Achsen in die neue Fläche legen: Flächen, die sich beim Umbau neigen, dürfen ihre Textur nicht verzerren
    const al = (f: { ua: Vec3; va: Vec3 }) => alignedAxes(nn, f.ua, f.va);
    if (bd > 0.995) return Object.assign({}, src, al(src), { vi: vi });
    const df = defaultFace(opts.tex || src.tex, nn);
    return Object.assign({ vi: vi }, df, al(df));
  });
  return { id: old.id, verts: h.verts, faces: faces };
}

export function bbox(b: Brush): BBox {
  const mn: Vec3 = [Infinity, Infinity, Infinity], mx: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const v of b.verts) for (let i = 0; i < 3; i++) { if (v[i] < mn[i]) mn[i] = v[i]; if (v[i] > mx[i]) mx[i] = v[i]; }
  return { min: mn, max: mx };
}
export function bboxOf(brushes: Brush[]): BBox {
  const mn: Vec3 = [Infinity, Infinity, Infinity], mx: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const b of brushes) { const bb = bbox(b); for (let i = 0; i < 3; i++) { if (bb.min[i] < mn[i]) mn[i] = bb.min[i]; if (bb.max[i] > mx[i]) mx[i] = bb.max[i]; } }
  return { min: mn, max: mx };
}

// ------------------------------------------------------------ Transformationen
/** p' = M p + t. lock: Textur bleibt an der Fläche haften (nur für Drehungen, Spiegelungen, Verschiebungen). */
export function transformBrush(b: Brush, M: Mat3, t: Vec3, lock?: boolean): Brush {
  const verts = b.verts.map((v) => add(mulMV(M, v), t));
  let faces = b.faces;
  const flip = det3(M) < 0;
  const MiT = transpose(inv3(M));
  faces = faces.map((f) => {
    const g = Object.assign({}, f);
    if (lock) {
      g.ua = mulMV(MiT, f.ua); g.va = mulMV(MiT, f.va);
      g.uo = f.uo - dot(t, g.ua) / f.xs; g.vo = f.vo - dot(t, g.va) / f.ys;
    }
    g.vi = flip ? f.vi.slice().reverse() : f.vi;
    return g;
  });
  return { id: b.id, verts: verts, faces: faces };
}
export function translateBrush(b: Brush, d: Vec3, lock?: boolean): Brush {
  const verts = b.verts.map((v): Vec3 => [v[0] + d[0], v[1] + d[1], v[2] + d[2]]);
  const faces = lock
    ? b.faces.map((f) => Object.assign({}, f, { uo: f.uo - dot(d, f.ua) / f.xs, vo: f.vo - dot(d, f.va) / f.ys }))
    : b.faces;
  return { id: b.id, verts: verts, faces: faces };
}
export function rotateAround(b: Brush, axis: number, deg: number, center: Vec3, lock?: boolean): Brush {
  const M = rotMat(axis, deg);
  return transformBrush(b, M, sub(center, mulMV(M, center)), lock);
}
export function mirrorAround(b: Brush, axis: number, center: Vec3, lock?: boolean): Brush {
  const M = mirrorMat(axis);
  return transformBrush(b, M, sub(center, mulMV(M, center)), lock);
}
/** Skalieren der Brush-Punkte von Box oldB nach newB (Rundung auf ganze Zahlen), Hülle neu. */
export function scaleBrush(b: Brush, oldB: BBox, newB: BBox, round?: boolean): Brush | null {
  const f = [0, 1, 2].map((i) => {
    const o = oldB.max[i] - oldB.min[i];
    return o < 1e-9 ? 1 : (newB.max[i] - newB.min[i]) / o;
  });
  const pts = b.verts.map((v): Vec3 => {
    const p = [0, 1, 2].map((i) => {
      const x = newB.min[i] + (v[i] - oldB.min[i]) * f[i];
      return round === false ? x : Math.round(x);
    });
    return [p[0], p[1], p[2]];
  });
  return rebuildBrush(b, pts);
}
export function moveVertices(b: Brush, moves: Map<number, Vec3>, tol?: number): Brush | null {
  // moves: Map index -> neue Position
  const pts = b.verts.map((v, i) => (moves.has(i) ? (moves.get(i) as Vec3) : v));
  return rebuildBrush(b, pts, { tol: tol });
}

// ------------------------------------------------------------ Clip
/** Schneidet an der Ebene n·p = d. keep: 'front' (n·p > d), 'back' oder 'both'. Gibt Array neuer Brushes zurück. */
export function clipBrush(b: Brush, n: Vec3, d: number, keep: 'front' | 'back' | 'both', cutTex?: string): Brush[] {
  const eps = 0.01;
  const s = b.verts.map((v) => dot(n, v) - d);
  const edges = new Set<string>();
  b.faces.forEach((f) => f.vi.forEach((a, i) => {
    const c = f.vi[(i + 1) % f.vi.length];
    edges.add(a < c ? a + ',' + c : c + ',' + a);
  }));
  const cuts: Vec3[] = [];
  edges.forEach((e) => {
    const [a, c] = e.split(',').map(Number);
    if ((s[a] > eps && s[c] < -eps) || (s[a] < -eps && s[c] > eps)) {
      const t = s[a] / (s[a] - s[c]);
      cuts.push([b.verts[a][0] + (b.verts[c][0] - b.verts[a][0]) * t, b.verts[a][1] + (b.verts[c][1] - b.verts[a][1]) * t, b.verts[a][2] + (b.verts[c][2] - b.verts[a][2]) * t]);
    }
  });
  const out: Brush[] = [];
  const side = (sign: number, id: string) => {
    const pts: Vec3[] = [];
    b.verts.forEach((v, i) => { if (s[i] * sign >= -eps) pts.push(v); });
    cuts.forEach((c) => pts.push(c));
    const r = rebuildBrush(b, pts, { tex: cutTex });
    if (r) { r.id = id; out.push(r); }
  };
  if (keep === 'front') side(1, b.id);
  else if (keep === 'back') side(-1, b.id);
  else { side(1, b.id); side(-1, newId()); }
  return out;
}
